/**
 * client/src/hooks/useBillingSubscription.ts
 *
 * React Query hooks for the new Razorpay subscription flow:
 *   useSubscriptionStatus  — GET  /api/billing/subscription
 *   useSubscribe           — POST /api/billing/subscribe → redirect to shortUrl
 *   useCancelSubscription  — POST /api/billing/subscription/cancel
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SubscriptionStatus {
  hasSubscription: boolean;
  status: "active" | "cancelled" | "pending_cancellation" | "halted" | "completed" | null;
  planCode: string;
  renewsAt: string | null;
  razorpayDetails?: {
    id: string;
    status: string;
    currentStart?: number;
    currentEnd?: number;
    chargeAt?: number;
    remainingCount?: number;
  };
}

export interface SubscribeResponse {
  subscriptionId: string;
  shortUrl: string | null;
}

// ── useSubscriptionStatus ─────────────────────────────────────────────────────
//
// Fetches the current user's subscription status and tier from the backend.
// Only runs when a user is authenticated.

export function useSubscriptionStatus() {
  const { user } = useAuth();

  return useQuery<SubscriptionStatus>({
    queryKey: ["/api/billing/subscription"],
    enabled: !!user,
    staleTime: 30_000, // refresh every 30 s
  });
}

// ── useSubscribe ──────────────────────────────────────────────────────────────
//
// Creates a Razorpay recurring subscription for the given plan.
// On success, redirects to Razorpay's hosted checkout page (shortUrl).

export function useSubscribe() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<SubscribeResponse, Error, "pro" | "enterprise">({
    mutationFn: async (planId) => {
      const res = await apiRequest("/api/billing/subscribe", "POST", { planId });
      return res.json();
    },
    onSuccess: ({ shortUrl, subscriptionId }) => {
      // Optimistically invalidate so the status card refreshes on return
      queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });

      if (shortUrl) {
        // Full-page redirect to Razorpay's hosted checkout
        window.location.href = shortUrl;
      } else {
        // Fallback: shortUrl missing — subscription created, tell user
        toast({
          title: "Subscription created",
          description: `Subscription ${subscriptionId} is ready. Complete payment in your Razorpay dashboard.`,
        });
      }
    },
    onError: (err) => {
      toast({
        title: "Subscription failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });
}

// ── useCancelSubscription ─────────────────────────────────────────────────────
//
// Cancels the current subscription at the end of the billing period.
// The user keeps access until the period ends.

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<{ message: string }, Error, void>({
    mutationFn: async () => {
      const res = await apiRequest("/api/billing/subscription/cancel", "POST", {});
      return res.json();
    },
    onSuccess: ({ message }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Subscription cancelled", description: message });
    },
    onError: (err) => {
      toast({
        title: "Cancellation failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });
}
